import { useMemo } from 'react'
import { useSettingsStore } from './useSettingsStore'

const RECENT_KEY = 'pta_recent_tickers'
const MAX_RECENT = 15

function parse(raw: string | undefined): string[] {
  if (raw == null) return []
  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((x): x is string => typeof x === 'string')
  } catch {
    return []
  }
}

function write(next: string[]) {
  useSettingsStore.getState().set(RECENT_KEY, JSON.stringify(next))
}

function readList(): string[] {
  return parse(useSettingsStore.getState().values[RECENT_KEY])
}

function addTicker(ticker: string) {
  const t = ticker.toUpperCase().trim()
  if (!t) return
  const current = readList()
  const filtered = current.filter((x) => x !== t)
  write([t, ...filtered].slice(0, MAX_RECENT))
}

function removeTicker(ticker: string) {
  const t = ticker.toUpperCase().trim()
  const current = readList()
  write(current.filter((x) => x !== t))
}

interface RecentTickersSnapshot {
  tickers: string[]
  add: (ticker: string) => void
  remove: (ticker: string) => void
}

// Mirrors the previous zustand selector API: useRecentTickersStore((s) => s.tickers).
// Backed by the settings store so the recent list now syncs across devices.
export function useRecentTickersStore<T>(selector: (s: RecentTickersSnapshot) => T): T {
  const tickers = useSettingsStore((s) => s.values[RECENT_KEY])
  const list = useMemo(() => parse(tickers), [tickers])
  const snapshot: RecentTickersSnapshot = {
    tickers: list,
    add: addTicker,
    remove: removeTicker,
  }
  return selector(snapshot)
}
